import { useSearchParams } from 'react-router-dom';

type UseQueriesControlOptions<T extends string> = {
	allowedValues?: Partial<{ [K in T]: string[] }>;
	defaultValue?: Partial<{ [K in T]: string }>;
};

export const useQueriesControl = <T extends string>(params: T[], { allowedValues, defaultValue }: UseQueriesControlOptions<T> = {}) => {
	const [search, setSearch] = useSearchParams();

	const queries = params.reduce(
		(acc, param) => {
			const value = search.get(param) || defaultValue?.[param] || '';
			const allowed = allowedValues?.[param];

			if (allowed && !allowed.includes(value)) {
				return acc;
			}

			return { ...acc, [param]: value };
		},
		{} as { [K in T]: string }
	);

	const onChange = <U extends Partial<{ [K in T]: string | number }>>(value: U) => {
		Object.entries(value).forEach(([key, value]) => {
			if (!params.includes(key as any) || (typeof value !== 'string' && typeof value !== 'number')) {
				return;
			}

			if (value === null || value === undefined) {
				search.delete(key);
			} else {
				search.set(key, value.toString());
			}
		});

		setSearch(search);
	};

	return [queries, onChange] as const;
};
